/**
 * Industrial POS Dashboard
 * Optimized for service staff - no scrolling for categories, large touch targets
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePOSStore } from '../stores/posStore';
import { useMenuStore } from '../stores/menuStore';
import { useNotificationStore } from '../stores/notificationStore';
import { useFloorPlanStore } from '../stores/floorPlanStore';
import { usePOSSessionStore } from '../stores/posSessionStore';
import { useRestaurantSettingsStore } from '../stores/restaurantSettingsStore';
import { useKDSStore } from '../stores/kdsStore';
import { MenuItem, OrderType, ComboSelection } from '../types/pos';
import { billService } from '../lib/billService';
import { salesTransactionService } from '../lib/salesTransactionService';
import { IndustrialModifierModal } from '../components/pos/IndustrialModifierModal';
import { IndustrialCheckoutModal } from '../components/pos/IndustrialCheckoutModal';
import { ComboSelectionModal } from '../components/pos/ComboSelectionModal';
import { PortionSelectionModal, needsPortionSelection } from '../components/pos/PortionSelectionModal';
import { BillPreviewModal } from '../components/pos/BillPreviewModal';
import { OnScreenKeyboard } from '../components/ui-v2/OnScreenKeyboard';
import { TableSelectorModal } from '../components/pos/TableSelectorModal';
import { StaffPinEntryModal } from '../components/pos/StaffPinEntryModal';
import { BillData } from '../components/print/BillPrint';
import { cn } from '../lib/utils';
import { useOutOfStockStore } from '../stores/outOfStockStore';
import { OutOfStockAlertModal } from '../components/alerts/OutOfStockAlertModal';
import type { OutOfStockAlert } from '../types/stock';
import { OrderBottomSheet } from '../components/pos/OrderBottomSheet';
import { useScreenSize } from '../hooks/useScreenSize';
import { useAggregatorStore } from '../stores/aggregatorStore';
import { CustomItemModal } from '../components/pos/CustomItemModal';
import { AggregatorOrdersDrawer } from '../components/pos/AggregatorOrdersDrawer';
import { Search, Package, PlusCircle, ChefHat, Clock } from 'lucide-react';

// Category icon helper
function getCategoryIcon(categoryName: string): string {
  const name = categoryName.toLowerCase();
  if (name.includes('appetizer') || name.includes('starter')) return '🥟';
  if (name.includes('main') || name.includes('entree') || name.includes('curry')) return '🍛';
  if (name.includes('combo')) return '🍱';
  if (name.includes('side')) return '🍚';
  if (name.includes('dessert') || name.includes('sweet')) return '🍰';
  if (name.includes('beverage') || name.includes('drink')) return '🥤';
  if (name.includes('rice')) return '🍚';
  if (name.includes('bread') || name.includes('roti')) return '🫓';
  if (name.includes('special')) return '⭐';
  return '🍽️';
}

export default function POSDashboard() {
  const { user } = useAuthStore();
  const {
    cart,
    selectedCategory,
    searchQuery,
    orderType,
    tableNumber,
    todaysSpecials,
    specialsLoaded,
    activeTables,
    setSelectedCategory,
    setSearchQuery,
    setOrderType,
    setTableNumber,
    openTable,
    addToCart,
    removeFromCart,
    submitOrder,
    sendToKitchen,
    getCartTotal,
    getFilteredMenu,
    getTableSession,
    loadTableSessions,
    loadTodaysSpecials,
    isKotPrintedForTable,
  } = usePOSStore();

  const { categories: menuCategories } = useMenuStore();
  const { sections, tables, loadFloorPlan, isLoaded: floorPlanLoaded, isLoading: floorPlanLoading } = useFloorPlanStore();
  const { activeStaff, isSessionValid } = usePOSSessionStore();
  const { settings } = useRestaurantSettingsStore();
  const { playSound } = useNotificationStore();
  // Subscribe to activeOrders/completedOrders to trigger re-renders when KDS state changes
  // The functions below use this state internally, but selecting it makes the component reactive
  const { activeOrders: _activeOrders, completedOrders: _completedOrders, areAllKotsCompletedForTable, hasAnyCompletedKotForTable, getItemStatusesForTable, getOrderStatusForTable, loadOrdersFromDb } = useKDSStore();
  // Mark as used to silence TS error (they trigger re-renders when KDS state changes)
  void _activeOrders; void _completedOrders;

  // Screen size for responsive layout
  const { isCompact } = useScreenSize();

  // Aggregator orders
  const { orders: aggregatorOrders, getStats: getAggregatorStats } = useAggregatorStore();
  const [showAggregatorPanel, setShowAggregatorPanel] = useState(false);
  const aggregatorStats = getAggregatorStats();

  // Custom item modal
  const [isCustomItemModalOpen, setIsCustomItemModalOpen] = useState(false);

  // Out of Stock alerts
  const { pendingAlerts, acknowledgeAlert } = useOutOfStockStore();
  const [currentOosAlert, setCurrentOosAlert] = useState<OutOfStockAlert | null>(null);

  // Settings
  const requireStaffPin = settings.posSettings?.requireStaffPinForPOS || false;
  const sessionTimeout = settings.posSettings?.pinSessionTimeoutMinutes || 0;
  const hasValidSession = !requireStaffPin || (activeStaff && isSessionValid(sessionTimeout));

  // Modal states
  const [isStaffPinModalOpen, setIsStaffPinModalOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null);
  const [isModifierModalOpen, setIsModifierModalOpen] = useState(false);
  const [isComboModalOpen, setIsComboModalOpen] = useState(false);
  const [isPortionModalOpen, setIsPortionModalOpen] = useState(false);
  const [isPlaceOrderModalOpen, setIsPlaceOrderModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isBillPreviewOpen, setIsBillPreviewOpen] = useState(false);
  const [generatedBillData, setGeneratedBillData] = useState<BillData | null>(null);
  const [generatedInvoiceNumber, setGeneratedInvoiceNumber] = useState('');

  // Keyboard state
  const [keyboardConfig, setKeyboardConfig] = useState<{
    isOpen: boolean;
    type: 'text' | 'number';
    value: string;
    title: string;
    onSave: (val: string) => void;
  }>({
    isOpen: false,
    type: 'text',
    value: '',
    title: '',
    onSave: () => {},
  });

  // Load data on mount - ALWAYS reload table sessions and KDS orders
  // This ensures data is fresh after navigation away and back
  useEffect(() => {
    const loadData = async () => {
      if (user?.tenantId) {
        console.log('[POSDashboard] Component mounted, loading data for tenant:', user.tenantId);
        // Always reload table sessions from SQLite on mount
        await loadTableSessions(user.tenantId);
        // Always reload KDS orders from SQLite (needed for billing eligibility check)
        await loadOrdersFromDb(user.tenantId);
        if (!floorPlanLoaded && !floorPlanLoading) {
          loadFloorPlan(user.tenantId);
        }
        // Load today's specials for quick billing
        if (!specialsLoaded) {
          loadTodaysSpecials(user.tenantId);
        }
      }
    };
    loadData();
  }, [user?.tenantId, loadTableSessions, loadOrdersFromDb]);

  // Show staff PIN modal if required
  useEffect(() => {
    if (requireStaffPin && !hasValidSession) {
      setIsStaffPinModalOpen(true);
    }
  }, [requireStaffPin, hasValidSession]);

  // Watch for new OOS alerts and show modal
  useEffect(() => {
    const unacknowledged = pendingAlerts.filter((a) => !a.acknowledged);
    if (unacknowledged.length > 0 && !currentOosAlert) {
      setCurrentOosAlert(unacknowledged[0]);
      playSound('order_urgent');
    }
  }, [pendingAlerts, currentOosAlert, playSound]);

  // Handle OOS alert acknowledgment
  const handleOosAcknowledge = () => {
    if (currentOosAlert) {
      acknowledgeAlert(currentOosAlert.id, activeStaff?.name);
      setCurrentOosAlert(null);
    }
  };

  // Current table info
  const currentTableInfo = useMemo(() => {
    if (tableNumber === null) return null;
    const table = tables.find(t => parseInt(t.tableNumber, 10) === tableNumber);
    if (!table) return { tableNumber, sectionName: null, capacity: null };
    const section = sections.find(s => s.id === table.sectionId);
    return {
      tableNumber,
      sectionName: section?.name || null,
      capacity: table.capacity,
    };
  }, [tableNumber, tables, sections]);

  const filteredMenu = getFilteredMenu();
  const cartTotals = getCartTotal();
  const activeTableSession = tableNumber ? getTableSession(tableNumber) : null;
  const activeTableOrder = activeTableSession?.order || null;

  // Billing check - requires KOT printed AND ALL KOTs completed (bumped) in KDS
  // Bill can only be generated after ALL KOTs are bumped/completed
  const canGenerateBill = (() => {
    if (!activeTableOrder && cart.length === 0) return false;
    if (orderType === 'dine-in' && tableNumber) {
      // Must have sent at least one KOT
      if (!isKotPrintedForTable(tableNumber)) return false;
      // ALL KOTs must be completed (bumped) - no active orders in KDS for this table
      if (!areAllKotsCompletedForTable(tableNumber)) return false;
      return true;
    }
    return true;
  })();

  // Build categories with "All" first, then "Today's Special" if specials exist
  const categories = useMemo(() => {
    const baseCategories = [
      { id: 'all', label: 'All', icon: '📋', isSpecial: false },
    ];

    // Add "Today's Special" category only if there are specials loaded
    if (todaysSpecials.length > 0) {
      baseCategories.push({
        id: 'todays-special',
        label: "TODAY'S",
        icon: '⭐',
        isSpecial: true,
      });
    }

    // Add regular menu categories
    const regularCategories = menuCategories.map((cat) => ({
      id: cat.id,
      label: cat.name.length > 12 ? cat.name.substring(0, 10) + '...' : cat.name,
      fullLabel: cat.name,
      icon: cat.icon || getCategoryIcon(cat.name),
      isSpecial: false,
    }));

    return [...baseCategories, ...regularCategories];
  }, [menuCategories, todaysSpecials]);

  const orderTypes: { id: OrderType; label: string; icon: string }[] = [
    { id: 'dine-in', label: 'DINE-IN', icon: '🪑' },
    { id: 'takeout', label: 'PICKUP', icon: '🥡' },
  ];

  // Can add items: dine-in requires table, pickup doesn't need table
  const canAddItems = orderType === 'takeout' || (orderType === 'dine-in' && tableNumber !== null);

  const handleMenuItemClick = (item: MenuItem) => {
    if (orderType === 'dine-in' && tableNumber === null) {
      setIsTableModalOpen(true);
      return;
    }

    // Debug: Log item properties to check combo detection
    console.log('[POSDashboard] Item clicked:', item.name, {
      isCombo: item.isCombo,
      comboGroups: item.comboGroups,
      comboGroupsLength: item.comboGroups?.length,
      hasModifiers: item.modifiers?.length,
    });

    if (item.isCombo && item.comboGroups && item.comboGroups.length > 0) {
      console.log('[POSDashboard] Opening combo modal for:', item.name);
      setSelectedMenuItem(item);
      setIsComboModalOpen(true);
    } else if (needsPortionSelection(item)) {
      // Rice items (Ney Kullu, Steamed Rice) - show portion selection
      setSelectedMenuItem(item);
      setIsPortionModalOpen(true);
    } else if (item.modifiers && item.modifiers.length > 0) {
      setSelectedMenuItem(item);
      setIsModifierModalOpen(true);
    } else {
      addToCart(item, 1, []);
      playSound('order_ready');
    }
  };

  const handleAddToCart = (
    menuItem: MenuItem,
    quantity: number,
    modifiers: any[],
    specialInstructions?: string,
    comboSelections?: ComboSelection[]
  ) => {
    addToCart(menuItem, quantity, modifiers, specialInstructions, comboSelections);
    playSound('order_ready');
  };

  const handleSendToKitchen = async () => {
    if (!user?.tenantId) return;
    try {
      await sendToKitchen(user.tenantId);
      playSound('order_ready');
    } catch (error) {
      console.error('Failed to send to kitchen:', error);
      alert(error instanceof Error ? error.message : 'Failed to send to kitchen');
    }
  };

  const handleGenerateBill = async (cashDiscount?: number) => {
    if (!user?.tenantId) return;
    if (orderType === 'dine-in' && tableNumber) {
      if (!isKotPrintedForTable(tableNumber)) {
        alert('Send KOT first before generating bill.');
        return;
      }
      // Either all KOTs completed OR at least one KOT completed
      if (!areAllKotsCompletedForTable(tableNumber) && !hasAnyCompletedKotForTable(tableNumber)) {
        alert('At least one KOT must be completed in kitchen before generating bill. Please wait for kitchen to finish preparing some items.');
        return;
      }
    }
    try {
      // Submit order with discount if provided
      const order = await submitOrder(user.tenantId, 'pending', cashDiscount);
      playSound('order_ready');
      const bill = billService.generateBill(order, user.name || 'Staff');

      // Record the sale immediately with 'pending' payment method
      // This ensures the sale is recorded even if the user closes the modal
      // Payment method can be updated later when selected
      try {
        await salesTransactionService.recordSale(
          user.tenantId,
          bill,
          'pending',
          activeStaff?.id,
          'pos'
        );
        console.log('[POSDashboard] Sale recorded with pending payment:', bill.invoiceNumber, 'discount:', cashDiscount || 0);
      } catch (saleError) {
        console.error('[POSDashboard] Failed to record sale:', saleError);
        // Don't block bill display if sale recording fails
      }

      setGeneratedBillData(bill.billData);
      setGeneratedInvoiceNumber(bill.invoiceNumber);
      setIsBillPreviewOpen(true);
    } catch (error) {
      console.error('Failed to generate bill:', error);
      alert('Failed to generate bill.');
    }
  };

  const openKeyboard = (type: 'text' | 'number', currentVal: string, title: string, onSave: (val: string) => void) => {
    setKeyboardConfig({ isOpen: true, type, value: currentVal, title, onSave });
  };

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const grandTotal = cartTotals.total + (activeTableOrder?.total || 0);

  return (
    <div className="h-screen w-screen bg-[#0d0d0d] flex flex-col overflow-hidden select-none">
      {/* ========== TOP BAR - Fixed 80px ========== */}
      <header className="h-20 flex-shrink-0 bg-gradient-to-b from-zinc-800 to-zinc-900 border-b-2 border-zinc-700 px-4 flex items-center gap-4 overflow-visible">
        {/* Table/Order Type Selector */}
        <div className="flex items-center gap-2">
          {/* Table Button */}
          <button
            onClick={() => setIsTableModalOpen(true)}
            className={cn(
              "h-14 px-4 rounded-xl border-2 flex items-center gap-3 transition-all",
              tableNumber !== null
                ? "bg-emerald-500/20 border-emerald-500 text-emerald-400"
                : "bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-400"
            )}
          >
            <span className="text-2xl">🪑</span>
            <div className="text-left">
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">TABLE</div>
              <div className="text-xl font-black font-mono">{tableNumber ?? '--'}</div>
            </div>
          </button>

          {/* Order Type Pills */}
          <div className="flex gap-1 bg-zinc-800 p-1 rounded-xl border-2 border-zinc-700">
            {orderTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setOrderType(type.id)}
                className={cn(
                  "h-12 px-4 rounded-lg font-black text-xs uppercase tracking-wide transition-all flex items-center gap-2",
                  orderType === type.id
                    ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                    : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700"
                )}
              >
                <span className="text-lg">{type.icon}</span>
                <span className="hidden sm:inline">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Staff Info (if PIN required) */}
        {activeStaff && (
          <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-xl border-2 border-zinc-700">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-black text-sm">
              {activeStaff.name.charAt(0)}
            </div>
            <div className="text-left">
              <div className="text-[10px] font-mono text-zinc-500 uppercase">STAFF</div>
              <div className="text-sm font-bold text-white">{activeStaff.name}</div>
            </div>
          </div>
        )}

        {/* Active Tables Quick View with Status */}
        {(() => {
          // Show ALL active tables (tables with a session, even without items)
          const activeTableNumbers = Object.keys(activeTables)
            .filter(key => {
              const session = activeTables[parseInt(key)];
              return session !== undefined;
            })
            .map(key => parseInt(key))
            .sort((a, b) => a - b);
          const activeCount = activeTableNumbers.length;

          // Debug logging
          console.log('[POSDashboard Header] activeTables:', activeTables);
          console.log('[POSDashboard Header] activeTableNumbers:', activeTableNumbers);
          console.log('[POSDashboard Header] activeCount:', activeCount);

          // Get status for each table
          const getTableStatus = (tbl: number) => {
            const hasKot = isKotPrintedForTable(tbl);
            if (!hasKot) return 'new'; // No KOT sent yet
            const allCompleted = areAllKotsCompletedForTable(tbl);
            if (allCompleted) return 'ready'; // Ready for billing
            return 'preparing'; // In kitchen
          };

          return (
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Active Tables Pills with Status */}
              {activeCount > 0 ? (
                <div className="flex items-center gap-1 px-3 py-2 bg-zinc-800 rounded-xl border-2 border-zinc-700">
                  <span className="text-xs font-bold text-zinc-400 uppercase">Open ({activeCount}):</span>
                  <div className="flex gap-1">
                    {activeTableNumbers.slice(0, 8).map((tbl) => {
                      const status = getTableStatus(tbl);
                      const isSelected = tableNumber === tbl;
                      return (
                        <button
                          key={tbl}
                          onClick={() => {
                            setTableNumber(tbl);
                            setOrderType('dine-in');
                          }}
                          className={cn(
                            "relative w-9 h-9 rounded-lg font-black text-sm flex items-center justify-center transition-all",
                            isSelected
                              ? status === 'ready'
                                ? "bg-emerald-500 text-white ring-2 ring-emerald-300"
                                : status === 'preparing'
                                ? "bg-amber-500 text-white ring-2 ring-amber-300"
                                : "bg-blue-500 text-white ring-2 ring-blue-300"
                              : status === 'ready'
                              ? "bg-emerald-500/30 text-emerald-300 border-2 border-emerald-500 hover:bg-emerald-500/50"
                              : status === 'preparing'
                              ? "bg-amber-500/30 text-amber-300 border-2 border-amber-500 hover:bg-amber-500/50"
                              : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                          )}
                          title={
                            status === 'ready' ? `Table ${tbl} - Ready for Bill`
                            : status === 'preparing' ? `Table ${tbl} - Preparing`
                            : `Table ${tbl} - New Order`
                          }
                        >
                          {tbl}
                          {/* Status dot indicator */}
                          {status === 'ready' && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-zinc-800 animate-pulse" />
                          )}
                          {status === 'preparing' && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border-2 border-zinc-800 animate-pulse" />
                          )}
                        </button>
                      );
                    })}
                    {activeCount > 8 && (
                      <span className="w-9 h-9 rounded-lg bg-zinc-700 text-zinc-400 font-bold text-xs flex items-center justify-center">
                        +{activeCount - 8}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="px-3 py-2 bg-zinc-800/50 rounded-xl border border-zinc-700 text-zinc-500 text-xs">
                  No open tables (keys: {Object.keys(activeTables).join(',') || 'none'})
                </div>
              )}

              {/* New items indicator (compact) */}
              {cartItemCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 rounded-xl border-2 border-emerald-500">
                  <span className="text-lg">📝</span>
                  <span className="font-black text-emerald-400 text-sm">{cartItemCount} new</span>
                </div>
              )}

              {/* Aggregator Orders Status Indicator */}
              {aggregatorOrders.length > 0 && (
                <button
                  onClick={() => setShowAggregatorPanel(!showAggregatorPanel)}
                  className={cn(
                    "relative flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all",
                    aggregatorStats.ready > 0
                      ? "bg-emerald-500/20 border-emerald-500/50 hover:bg-emerald-500/30 animate-pulse"
                      : aggregatorStats.preparing > 0
                      ? "bg-orange-500/20 border-orange-500/50 hover:bg-orange-500/30"
                      : aggregatorStats.new > 0
                      ? "bg-amber-500/20 border-amber-500/50 hover:bg-amber-500/30 animate-pulse"
                      : "bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
                  )}
                >
                  {/* Status icon based on priority */}
                  {aggregatorStats.ready > 0 ? (
                    <Clock size={18} className="text-emerald-400" />
                  ) : aggregatorStats.preparing > 0 ? (
                    <ChefHat size={18} className="text-orange-400" />
                  ) : (
                    <Package size={18} className="text-amber-400" />
                  )}

                  {/* Order count */}
                  <span className={cn(
                    "font-black text-sm",
                    aggregatorStats.ready > 0
                      ? "text-emerald-400"
                      : aggregatorStats.preparing > 0
                      ? "text-orange-400"
                      : "text-amber-400"
                  )}>
                    {aggregatorOrders.filter(o => !['delivered', 'completed', 'cancelled'].includes(o.status)).length}
                  </span>

                  {/* Status badges */}
                  <div className="hidden sm:flex items-center gap-1">
                    {aggregatorStats.new > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/30 text-amber-400 text-[10px] font-bold">
                        {aggregatorStats.new} NEW
                      </span>
                    )}
                    {aggregatorStats.preparing > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-orange-500/30 text-orange-400 text-[10px] font-bold">
                        {aggregatorStats.preparing} 🍳
                      </span>
                    )}
                    {aggregatorStats.ready > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-400 text-[10px] font-bold animate-pulse">
                        {aggregatorStats.ready} READY
                      </span>
                    )}
                  </div>
                </button>
              )}

              {/* Custom Item Button - Only show for dine-in with table selected */}
              {orderType === 'dine-in' && tableNumber !== null && (
                <button
                  onClick={() => setIsCustomItemModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-500/20 border border-purple-500/50 text-purple-400 hover:bg-purple-500/30 transition-all"
                  title="Add custom item"
                >
                  <PlusCircle size={18} />
                  <span className="text-xs font-bold uppercase hidden sm:inline">Custom</span>
                </button>
              )}

              {/* Search Button */}
              <button
                onClick={() => {
                  openKeyboard('text', searchQuery, 'Search Menu', (val) => {
                    setSearchQuery(val);
                  });
                }}
                className={cn(
                  "relative p-2 rounded-lg transition-all",
                  searchQuery
                    ? "bg-emerald-500/20 border border-emerald-500/50 text-emerald-400"
                    : "bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-white"
                )}
                title={searchQuery || "Search menu"}
              >
                <Search size={20} />
                {searchQuery && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-500" />
                )}
              </button>
            </div>
          );
        })()}
      </header>

      {/* ========== CATEGORY BAR - Square full-width buttons ========== */}
      <nav className="flex-shrink-0 bg-zinc-900 border-b-2 border-zinc-700">
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12">
          {categories.slice(0, 24).map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              title={(category as any).fullLabel || category.label}
              className={cn(
                "h-12 border-r border-b border-zinc-700 flex items-center justify-center transition-all",
                // Special amber styling for Today's Special category
                category.isSpecial && selectedCategory === category.id
                  ? "bg-amber-500/30 text-amber-300 font-black"
                  : category.isSpecial && selectedCategory !== category.id
                  ? "bg-amber-500/10 text-amber-400/80 hover:bg-amber-500/20 hover:text-amber-300"
                  : selectedCategory === category.id
                  ? "bg-emerald-500/30 text-emerald-300 font-black"
                  : "bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700 hover:text-white"
              )}
            >
              <span className="text-sm font-bold uppercase tracking-wide text-center px-1">
                {category.label}
              </span>
            </button>
          ))}
        </div>
      </nav>

      {/* ========== MAIN CONTENT - Menu Grid + Cart Sidebar ========== */}
      <div className="flex-1 flex overflow-hidden">
        {/* Menu Items Grid */}
        <main className="flex-1 overflow-y-auto p-4 bg-[#0a0a0a]">
          {/* Table selection prompt */}
          {!canAddItems && (
            <button
              onClick={() => setIsTableModalOpen(true)}
              className="w-full mb-4 h-16 bg-amber-500/20 border-2 border-amber-500 rounded-xl flex items-center justify-center gap-3 hover:bg-amber-500/30 transition-colors"
            >
              <span className="text-3xl">🪑</span>
              <span className="font-black text-amber-400 uppercase tracking-wide">SELECT TABLE TO START</span>
              <span className="text-2xl">→</span>
            </button>
          )}

          {filteredMenu.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-zinc-500">
              <span className="text-6xl mb-4 opacity-50">🍽️</span>
              <p className="font-black uppercase tracking-widest">No items found</p>
            </div>
          ) : (() => {
            // Categorize items into Veg and Non-Veg
            const isVegItem = (item: MenuItem) => {
              // Check tags for veg/vegetarian
              if (item.tags?.includes('veg') || item.tags?.includes('vegetarian')) return true;
              if (item.tags?.includes('non-veg')) return false;
              // Check item properties for veg indicators
              if ((item as any).is_veg || (item as any).isVeg) return true;
              // Check dietary_tags (from database)
              const dietaryTags = (item as any).dietary_tags;
              if (Array.isArray(dietaryTags) && (dietaryTags.includes('veg') || dietaryTags.includes('vegetarian'))) return true;
              // Check item name for (V) suffix which indicates vegetarian
              if (item.name.includes('(V)')) return true;
              return false;
            };

            const isNonVegItem = (item: MenuItem) => {
              if (item.tags?.includes('non-veg')) return true;
              // Check name for meat keywords (including Kodava/local terms)
              const name = item.name.toLowerCase();
              return name.includes('chicken') || name.includes('mutton') || name.includes('pork') ||
                     name.includes('fish') || name.includes('prawn') || name.includes('egg') ||
                     name.includes('lamb') || name.includes('beef') || name.includes('meat') ||
                     name.includes('keema') || name.includes('kheema') || name.includes('gosht') ||
                     name.includes('murgh') || name.includes('macchi') || name.includes('jhinga') ||
                     // Kodava/local terms
                     name.includes('koli') ||      // Chicken in Kodava
                     name.includes('pandi') ||     // Pork in Kodava
                     name.includes('erachi') ||    // Meat in Malayalam/Kodava
                     name.includes('kaima') ||     // Keema/minced meat
                     name.includes('mutte');       // Egg in Kannada/Kodava
            };

            // Get non-veg subcategory (only chicken, mutton, pork - rest goes to other)
            const getNonVegCategory = (item: MenuItem): 'chicken' | 'mutton' | 'pork' | 'other' => {
              const name = item.name.toLowerCase();
              // Chicken (including Kodava term 'koli')
              if (name.includes('chicken') || name.includes('murgh') || name.includes('murg') || name.includes('koli')) return 'chicken';
              // Mutton (including keema, erachi for general meat)
              if (name.includes('mutton') || name.includes('lamb') || name.includes('gosht') ||
                  name.includes('keema') || name.includes('kheema') || name.includes('kaima') ||
                  name.includes('erachi')) return 'mutton';
              // Pork (including Kodava term 'pandi')
              if (name.includes('pork') || name.includes('bacon') || name.includes('ham') ||
                  name.includes('sausage') || name.includes('pandi')) return 'pork';
              return 'other';
            };

            const vegItems = filteredMenu.filter(isVegItem);
            const nonVegItems = filteredMenu.filter(item => !isVegItem(item) && isNonVegItem(item));
            const uncategorizedItems = filteredMenu.filter(item => !isVegItem(item) && !isNonVegItem(item));

            // Group non-veg by subcategory (chicken, mutton, pork only)
            const chickenItems = nonVegItems.filter(item => getNonVegCategory(item) === 'chicken');
            const muttonItems = nonVegItems.filter(item => getNonVegCategory(item) === 'mutton');
            const porkItems = nonVegItems.filter(item => getNonVegCategory(item) === 'pork');
            const otherNonVegItems = nonVegItems.filter(item => getNonVegCategory(item) === 'other');

            // Render menu item card
            const renderMenuCard = (item: MenuItem) => (
              <button
                key={item.id}
                onClick={() => handleMenuItemClick(item)}
                disabled={!canAddItems}
                className={cn(
                  "relative p-3 rounded-xl border-2 text-left transition-all group",
                  "min-h-[100px] flex flex-col justify-between",
                  canAddItems
                    ? "bg-zinc-800/80 border-zinc-600 hover:border-emerald-500 hover:bg-zinc-700 active:scale-[0.98] shadow-lg"
                    : "bg-zinc-900 border-zinc-800 opacity-50 cursor-not-allowed"
                )}
              >
                {/* Veg/Non-veg indicator - top left */}
                <div className="absolute top-2 left-2">
                  {isVegItem(item) && (
                    <div className="w-4 h-4 rounded border-2 border-emerald-500 bg-emerald-500/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    </div>
                  )}
                  {!isVegItem(item) && isNonVegItem(item) && (
                    <div className="w-4 h-4 rounded border-2 border-red-500 bg-red-500/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                    </div>
                  )}
                </div>

                {/* Combo Badge - top right */}
                {item.isCombo && (
                  <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-purple-500/30 border border-purple-500 rounded text-[8px] font-black text-purple-300 uppercase">
                    COMBO
                  </span>
                )}

                {/* Item Name */}
                <div className="flex-1 pt-5">
                  <h3 className="font-bold text-sm text-white leading-snug line-clamp-2 group-hover:text-emerald-300 transition-colors">
                    {item.name}
                  </h3>
                </div>

                {/* Price */}
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-black text-base text-emerald-400 font-mono">₹{item.price}</span>
                  <div className="w-7 h-7 rounded-lg bg-zinc-600 border-2 border-zinc-500 flex items-center justify-center group-hover:bg-emerald-500 group-hover:border-emerald-400 transition-all">
                    <span className="text-lg font-bold text-zinc-300 group-hover:text-white">+</span>
                  </div>
                </div>
              </button>
            );

            // Render a subcategory section
            const renderSubcategory = (title: string, items: MenuItem[], icon: string, borderColor: string) => {
              if (items.length === 0) return null;
              return (
                <div key={title} className="mb-3">
                  <div className={cn("flex items-center gap-2 px-2 py-1.5 rounded-lg mb-2", borderColor)}>
                    <span className="text-sm">{icon}</span>
                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-300">{title}</span>
                    <span className="text-[10px] font-mono text-zinc-500">({items.length})</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {items.map(renderMenuCard)}
                  </div>
                </div>
              );
            };

            const hasVeg = vegItems.length > 0;
            const hasNonVeg = nonVegItems.length > 0;
            const hasUncategorized = uncategorizedItems.length > 0;

            // If only one type exists, show single column with full width
            if (!hasNonVeg && hasVeg) {
              return (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                    <div className="w-5 h-5 rounded border-2 border-emerald-500 bg-emerald-500/20 flex items-center justify-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    </div>
                    <span className="font-black text-emerald-400 uppercase tracking-widest text-xs">Vegetarian</span>
                    <span className="text-xs font-mono text-emerald-400/60">({vegItems.length})</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {vegItems.map(renderMenuCard)}
                  </div>
                  {hasUncategorized && (
                    <>
                      <div className="flex items-center gap-2 px-3 py-2 bg-zinc-500/10 border border-zinc-500/30 rounded-xl mt-4">
                        <span className="font-black text-zinc-400 uppercase tracking-widest text-xs">Other Items</span>
                        <span className="text-xs font-mono text-zinc-400/60">({uncategorizedItems.length})</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                        {uncategorizedItems.map(renderMenuCard)}
                      </div>
                    </>
                  )}
                </div>
              );
            }

            if (!hasVeg && hasNonVeg) {
              return (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <div className="w-5 h-5 rounded border-2 border-red-500 bg-red-500/20 flex items-center justify-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    </div>
                    <span className="font-black text-red-400 uppercase tracking-widest text-xs">Non-Vegetarian</span>
                    <span className="text-xs font-mono text-red-400/60">({nonVegItems.length})</span>
                  </div>
                  <div className="space-y-4">
                    {renderSubcategory('Chicken', chickenItems, '🍗', 'bg-orange-500/10 border border-orange-500/20')}
                    {renderSubcategory('Mutton', muttonItems, '🍖', 'bg-rose-500/10 border border-rose-500/20')}
                    {renderSubcategory('Pork', porkItems, '🥓', 'bg-pink-500/10 border border-pink-500/20')}
                    {renderSubcategory('Other', otherNonVegItems, '🍽️', 'bg-zinc-500/10 border border-zinc-500/20')}
                  </div>
                  {hasUncategorized && (
                    <>
                      <div className="flex items-center gap-2 px-3 py-2 bg-zinc-500/10 border border-zinc-500/30 rounded-xl mt-4">
                        <span className="font-black text-zinc-400 uppercase tracking-widest text-xs">Other Items</span>
                        <span className="text-xs font-mono text-zinc-400/60">({uncategorizedItems.length})</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                        {uncategorizedItems.map(renderMenuCard)}
                      </div>
                    </>
                  )}
                </div>
              );
            }

            // Two column layout: Veg on left, Non-Veg on right (stacks on small screens < 900px)
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* VEG COLUMN */}
                <div className="space-y-3">
                  <div className="sticky top-0 z-10 bg-[#0a0a0a] pb-2">
                    <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                      <div className="w-5 h-5 rounded border-2 border-emerald-500 bg-emerald-500/20 flex items-center justify-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      </div>
                      <span className="font-black text-emerald-400 uppercase tracking-widest text-xs">VEG</span>
                      <span className="text-xs font-mono text-emerald-400/60">({vegItems.length})</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-2">
                    {vegItems.map(renderMenuCard)}
                  </div>
                </div>

                {/* NON-VEG COLUMN */}
                <div className="space-y-3 lg:border-l lg:border-zinc-700 lg:pl-4">
                  <div className="sticky top-0 z-10 bg-[#0a0a0a] pb-2">
                    <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-xl">
                      <div className="w-5 h-5 rounded border-2 border-red-500 bg-red-500/20 flex items-center justify-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      </div>
                      <span className="font-black text-red-400 uppercase tracking-widest text-xs">NON-VEG</span>
                      <span className="text-xs font-mono text-red-400/60">({nonVegItems.length})</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {renderSubcategory('Chicken', chickenItems, '🍗', 'bg-orange-500/10 border border-orange-500/20')}
                    {renderSubcategory('Mutton', muttonItems, '🍖', 'bg-rose-500/10 border border-rose-500/20')}
                    {renderSubcategory('Pork', porkItems, '🥓', 'bg-pink-500/10 border border-pink-500/20')}
                    {renderSubcategory('Other', otherNonVegItems, '🍽️', 'bg-zinc-500/10 border border-zinc-500/20')}
                  </div>
                </div>

                {/* Uncategorized items below spanning both columns */}
                {hasUncategorized && (
                  <div className="col-span-1 lg:col-span-2 mt-4">
                    <div className="flex items-center gap-2 px-3 py-2 bg-zinc-500/10 border border-zinc-500/30 rounded-xl">
                      <span className="font-black text-zinc-400 uppercase tracking-widest text-xs">Other Items</span>
                      <span className="text-xs font-mono text-zinc-400/60">({uncategorizedItems.length})</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                      {uncategorizedItems.map(renderMenuCard)}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </main>

        {/* ========== RIGHT SIDEBAR - Order ========== */}
        <aside className="hidden md:flex w-72 lg:w-80 xl:w-96 2xl:w-[420px] flex-shrink-0 bg-zinc-900 border-l-2 border-zinc-700 flex-col">
          {/* Order Header */}
          <div className="flex-shrink-0 p-4 border-b-2 border-zinc-700 bg-gradient-to-b from-zinc-800 to-zinc-900">
            <div className="flex items-center justify-between">
              <h2 className="font-black uppercase tracking-widest text-sm text-white flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                ORDER
              </h2>
              {cart.length > 0 && (
                <button
                  onClick={() => usePOSStore.getState().clearCart()}
                  className="text-[10px] font-bold text-red-400 hover:text-red-300 uppercase tracking-wide px-3 py-1 rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-all"
                >
                  CLEAR
                </button>
              )}
            </div>

            {/* Table Info for Dine-in */}
            {orderType === 'dine-in' && tableNumber !== null && (
              <div className="mt-3 p-3 bg-zinc-800 rounded-xl border border-zinc-700 flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-500/20 border-2 border-emerald-500 rounded-xl flex flex-col items-center justify-center">
                  <span className="text-[8px] font-mono text-emerald-400">TBL</span>
                  <span className="text-lg font-black text-emerald-400">{tableNumber}</span>
                </div>
                <div className="flex-1">
                  {currentTableInfo?.sectionName && (
                    <div className="text-[10px] font-mono text-emerald-400 uppercase">{currentTableInfo.sectionName}</div>
                  )}
                  <div className="text-xs text-zinc-400">
                    {activeTableSession?.guestCount || 1} guest{(activeTableSession?.guestCount || 1) !== 1 ? 's' : ''}
                    {currentTableInfo?.capacity && ` • ${currentTableInfo.capacity} seats`}
                  </div>
                </div>
                {activeTableOrder && (
                  <span className="px-2 py-1 bg-amber-500/20 border border-amber-500 rounded text-[10px] font-black text-amber-400 uppercase">
                    ACTIVE
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Order Items - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* NEW ITEMS - Show at top (editable, not yet sent to kitchen) */}
            {cart.length > 0 && (
              <div className="space-y-2">
                <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest px-1 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  NEW ITEMS
                </div>
                {cart.map((item) => (
                  <div key={item.id} className="relative px-3 py-2 bg-zinc-800 rounded-xl border border-emerald-500/50">
                    {/* Close button - centered on right edge */}
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="absolute -right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-white text-xs font-bold hover:bg-red-600 shadow-md z-10"
                    >
                      ×
                    </button>

                    {/* Item row: Qty, Name, Price */}
                    <div className="flex items-center gap-2 pr-4">
                      {/* Quantity badge */}
                      <span className="w-6 h-6 rounded bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center text-xs font-black text-emerald-400">
                        {item.quantity}
                      </span>
                      {/* Name */}
                      <span className="flex-1 font-bold text-sm text-white truncate">{item.menuItem.name}</span>
                      {/* Price */}
                      <span className="font-black text-emerald-400 font-mono text-sm">₹{item.subtotal.toFixed(0)}</span>
                    </div>

                    {/* Combo Selections - compact */}
                    {item.comboSelections && item.comboSelections.length > 0 && (
                      <div className="mt-1.5 pl-8 flex flex-wrap gap-1">
                        {item.comboSelections.flatMap((group) =>
                          group.selectedItems.map((sel, idx) => (
                            <span key={`${group.groupId}-${idx}`} className="text-[9px] bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/30">
                              {sel.name}
                            </span>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* SENT TO KITCHEN - Items already sent (non-editable, shown with status from KDS) */}
            {/* Only show when table has active KOTs in kitchen (not all completed) */}
            {activeTableOrder && activeTableOrder.items.length > 0 && tableNumber && !areAllKotsCompletedForTable(tableNumber) && (() => {
              // Get item statuses from KDS
              const itemStatuses = getItemStatusesForTable(tableNumber);
              const orderStatus = getOrderStatusForTable(tableNumber);

              // Status config for display
              const getStatusDisplay = (itemName: string) => {
                const status = itemStatuses.get(itemName) || 'pending';
                switch (status) {
                  case 'ready':
                    return { text: 'READY', color: 'text-emerald-400', bgColor: 'bg-emerald-500', animate: true };
                  case 'in_progress':
                    return { text: 'PREPARING', color: 'text-blue-400', bgColor: 'bg-blue-500', animate: true };
                  case 'pending':
                  default:
                    return { text: 'WAITING', color: 'text-amber-400/70', bgColor: 'bg-amber-500/50', animate: false };
                }
              };

              // Header status badge
              const getHeaderStatus = () => {
                if (orderStatus.status === 'ready') return { text: 'ALL READY', color: 'bg-emerald-500', animate: true };
                if (orderStatus.status === 'in_progress') return { text: 'PREPARING', color: 'bg-blue-500', animate: true };
                return { text: 'IN KITCHEN', color: 'bg-amber-500', animate: true };
              };

              const headerStatus = getHeaderStatus();

              return (
                <div className="space-y-2">
                  <div className={cn(
                    "flex items-center gap-2 px-1 py-2 rounded-lg border",
                    orderStatus.status === 'ready'
                      ? "bg-emerald-500/10 border-emerald-500/30"
                      : orderStatus.status === 'in_progress'
                        ? "bg-blue-500/10 border-blue-500/30"
                        : "bg-amber-500/10 border-amber-500/30"
                  )}>
                    <span className={cn(
                      "w-3 h-3 rounded-full shadow-lg",
                      headerStatus.color,
                      headerStatus.animate && "animate-pulse"
                    )} />
                    <span className={cn(
                      "text-xs font-black uppercase tracking-widest",
                      orderStatus.status === 'ready' ? "text-emerald-400" : orderStatus.status === 'in_progress' ? "text-blue-400" : "text-amber-400"
                    )}>
                      {headerStatus.text} ({orderStatus.readyItemCount}/{orderStatus.totalItemCount})
                    </span>
                  </div>
                  {activeTableOrder.items.map((item, idx) => {
                    const statusDisplay = getStatusDisplay(item.menuItem.name);
                    return (
                      <div key={`active-${idx}`} className={cn(
                        "p-3 rounded-xl border-2 flex justify-between items-center",
                        statusDisplay.text === 'READY'
                          ? "bg-emerald-500/5 border-emerald-500/40"
                          : statusDisplay.text === 'PREPARING'
                            ? "bg-blue-500/5 border-blue-500/40"
                            : "bg-amber-500/5 border-amber-500/40"
                      )}>
                        <div className="flex-1 min-w-0 flex items-center gap-3">
                          <div className={cn(
                            "w-2 h-8 rounded-full",
                            statusDisplay.bgColor,
                            statusDisplay.animate && "animate-pulse"
                          )} />
                          <div>
                            <div className="text-sm font-bold text-white truncate">{item.menuItem.name}</div>
                            <div className={cn("text-xs font-mono", statusDisplay.color)}>
                              × {item.quantity} • {statusDisplay.text}
                            </div>
                          </div>
                        </div>
                        <div className={cn(
                          "text-base font-black font-mono",
                          statusDisplay.text === 'READY' ? "text-emerald-400" : statusDisplay.text === 'PREPARING' ? "text-blue-400" : "text-amber-400"
                        )}>
                          ₹{item.subtotal.toFixed(0)}
                        </div>
                      </div>
                    );
                  })}
                  {/* Running Order Total */}
                  <div className={cn(
                    "flex justify-between items-center px-3 py-2 rounded-lg border",
                    orderStatus.status === 'ready'
                      ? "bg-emerald-500/10 border-emerald-500/30"
                      : orderStatus.status === 'in_progress'
                        ? "bg-blue-500/10 border-blue-500/30"
                        : "bg-amber-500/10 border-amber-500/30"
                  )}>
                    <span className={cn(
                      "text-xs font-bold uppercase",
                      orderStatus.status === 'ready' ? "text-emerald-400/70" : orderStatus.status === 'in_progress' ? "text-blue-400/70" : "text-amber-400/70"
                    )}>Running Total</span>
                    <span className={cn(
                      "text-base font-black font-mono",
                      orderStatus.status === 'ready' ? "text-emerald-400" : orderStatus.status === 'in_progress' ? "text-blue-400" : "text-amber-400"
                    )}>₹{activeTableOrder.total.toFixed(0)}</span>
                  </div>
                </div>
              );
            })()}

            {/* READY FOR BILLING - Show when all KOTs completed (items served) */}
            {activeTableOrder && activeTableOrder.items.length > 0 && tableNumber && areAllKotsCompletedForTable(tableNumber) && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <span className="w-3 h-3 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/50" />
                  <span className="text-xs font-black text-emerald-400 uppercase tracking-widest">
                    READY FOR BILLING ({activeTableOrder.items.length} items)
                  </span>
                </div>
                {activeTableOrder.items.map((item, idx) => (
                  <div key={`billed-${idx}`} className="p-3 bg-emerald-500/5 rounded-xl border-2 border-emerald-500/40 flex justify-between items-center">
                    <div className="flex-1 min-w-0 flex items-center gap-3">
                      <div className="w-2 h-8 rounded-full bg-emerald-500" />
                      <div>
                        <div className="text-sm font-bold text-white truncate">{item.menuItem.name}</div>
                        <div className="text-xs text-emerald-400/70 font-mono">× {item.quantity} • SERVED</div>
                      </div>
                    </div>
                    <div className="text-base font-black text-emerald-400 font-mono">₹{item.subtotal.toFixed(0)}</div>
                  </div>
                ))}
                {/* Billing Total */}
                <div className="flex justify-between items-center px-3 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <span className="text-xs font-bold text-emerald-400/70 uppercase">Bill Total</span>
                  <span className="text-base font-black text-emerald-400 font-mono">₹{activeTableOrder.total.toFixed(0)}</span>
                </div>
              </div>
            )}

            {/* Empty state - only show if both new items and active order are empty */}
            {cart.length === 0 && (!activeTableOrder || activeTableOrder.items.length === 0) && (
              <div className="h-full flex flex-col items-center justify-center text-zinc-600">
                <span className="text-5xl mb-3 opacity-30">📋</span>
                <p className="font-black uppercase tracking-widest text-xs">Add items</p>
              </div>
            )}
          </div>

          {/* Order Footer - Actions */}
          <div className="flex-shrink-0 p-4 border-t-2 border-zinc-700 bg-gradient-to-t from-zinc-800 to-zinc-900 space-y-3">
            {/* Total */}
            <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-xl border-2 border-zinc-700">
              <span className="font-bold text-zinc-400 uppercase text-sm">Grand Total</span>
              <span className="text-3xl font-black text-white font-mono">₹{grandTotal.toFixed(0)}</span>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                disabled={cart.length === 0}
                onClick={handleSendToKitchen}
                className={cn(
                  "h-14 rounded-xl font-black uppercase tracking-wide text-sm transition-all border-2",
                  cart.length === 0
                    ? "bg-zinc-800 border-zinc-700 text-zinc-600 cursor-not-allowed"
                    : "bg-amber-500/20 border-amber-500 text-amber-400 hover:bg-amber-500/30 active:scale-95"
                )}
              >
                📋 SEND KOT
              </button>
              <button
                disabled={!canGenerateBill}
                onClick={() => setIsPlaceOrderModalOpen(true)}
                className={cn(
                  "h-14 rounded-xl font-black uppercase tracking-wide text-sm transition-all border-2",
                  canGenerateBill
                    ? "bg-emerald-500 border-emerald-400 text-white hover:bg-emerald-400 active:scale-95 shadow-lg shadow-emerald-500/30"
                    : "bg-zinc-800 border-zinc-700 text-zinc-600 cursor-not-allowed"
                )}
              >
                💵 BILL
              </button>
            </div>
          </div>
        </aside>

        {/* ========== MOBILE BOTTOM SHEET (shown on compact screens) ========== */}
        {isCompact && (
          <OrderBottomSheet
            cart={cart}
            grandTotal={grandTotal}
            activeTableOrder={activeTableOrder}
            tableNumber={tableNumber}
            orderType={orderType}
            canGenerateBill={canGenerateBill}
            onSendToKitchen={handleSendToKitchen}
            onBill={() => setIsPlaceOrderModalOpen(true)}
            itemStatuses={tableNumber ? getItemStatusesForTable(tableNumber) : undefined}
            orderStatus={tableNumber ? getOrderStatusForTable(tableNumber) : undefined}
            areAllKotsCompleted={tableNumber ? areAllKotsCompletedForTable(tableNumber) : false}
          />
        )}
      </div>

      {/* ========== MODALS ========== */}
      <IndustrialModifierModal
        isOpen={isModifierModalOpen}
        onClose={() => {
          setIsModifierModalOpen(false);
          setSelectedMenuItem(null);
        }}
        menuItem={selectedMenuItem}
        onAddToCart={handleAddToCart}
      />

      <ComboSelectionModal
        isOpen={isComboModalOpen}
        onClose={() => {
          setIsComboModalOpen(false);
          setSelectedMenuItem(null);
        }}
        menuItem={selectedMenuItem}
        onAddToCart={handleAddToCart}
      />

      <PortionSelectionModal
        isOpen={isPortionModalOpen}
        onClose={() => {
          setIsPortionModalOpen(false);
          setSelectedMenuItem(null);
        }}
        menuItem={selectedMenuItem}
        onAddToCart={handleAddToCart}
      />

      <IndustrialCheckoutModal
        isOpen={isPlaceOrderModalOpen}
        onClose={() => setIsPlaceOrderModalOpen(false)}
        subtotal={cartTotals.subtotal + (activeTableOrder?.subtotal || 0)}
        tax={cartTotals.tax + (activeTableOrder?.tax || 0)}
        total={cartTotals.total + (activeTableOrder?.total || 0)}
        orderType={orderType}
        tableNumber={tableNumber}
        cartItems={cart}
        onGenerateBill={handleGenerateBill}
      />

      <TableSelectorModal
        isOpen={isTableModalOpen}
        onClose={() => setIsTableModalOpen(false)}
        onSelect={(tableNum, guestCount) => {
          if (guestCount) {
            // New table with guest count - use openTable
            openTable(tableNum, guestCount, user?.tenantId);
          } else {
            // Existing active table - just select it
            setTableNumber(tableNum);
          }
          setOrderType('dine-in');
        }}
        currentTableNumber={tableNumber}
      />

      <OnScreenKeyboard
        isOpen={keyboardConfig.isOpen}
        onClose={() => setKeyboardConfig({ ...keyboardConfig, isOpen: false })}
        type={keyboardConfig.type}
        value={keyboardConfig.value}
        title={keyboardConfig.title}
        onChange={(val) => {
          setKeyboardConfig({ ...keyboardConfig, value: val });
          keyboardConfig.onSave(val);
        }}
      />

      <BillPreviewModal
        isOpen={isBillPreviewOpen}
        onClose={() => setIsBillPreviewOpen(false)}
        billData={generatedBillData}
        invoiceNumber={generatedInvoiceNumber}
      />

      <StaffPinEntryModal
        isOpen={isStaffPinModalOpen}
        onClose={() => {
          if (hasValidSession || !requireStaffPin) {
            setIsStaffPinModalOpen(false);
          }
        }}
        onSuccess={() => setIsStaffPinModalOpen(false)}
      />

      {/* Out of Stock Alert Modal */}
      <OutOfStockAlertModal
        alert={currentOosAlert}
        onAcknowledge={handleOosAcknowledge}
      />

      {/* Custom Item Modal */}
      <CustomItemModal
        isOpen={isCustomItemModalOpen}
        onClose={() => setIsCustomItemModalOpen(false)}
        onAdd={(menuItem, quantity) => {
          addToCart(menuItem, quantity, []);
          playSound('order_ready');
        }}
      />

      {/* Aggregator Orders Drawer */}
      <AggregatorOrdersDrawer
        isOpen={showAggregatorPanel}
        onClose={() => setShowAggregatorPanel(false)}
      />
    </div>
  );
}
